import React, { useContext, useState, useRef, useCallback, useEffect } from 'react';
// Util
import { useParams, useNavigate, useLocation } from 'react-router';
import useProjectStore from '../../../../../ProjectCanvas/useProjectStore';
// Canvas
import { CanvasContext } from '../../../WorkflowCanvas';
// Custom components
import CanvasReloadDialog from '../../../CanvasReloadDialog';
import ComponentSettingsToolbar from './ComponentNodeUtil/ComponentSettingsToolbar';
import ComponentNodeView from './ComponentNodeUtil/ComponentNodeView';
// MUI
import { withStyles } from '@mui/styles';
// Icons
import Error from '@mui/icons-material/Error';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PreviewIcon from '@mui/icons-material/Preview';
import EditIcon from '@mui/icons-material/Edit';
// Zustand
import useCanvasStore from '../../../useCanvasStore';
import InstructionsInfo from '../../../InstructionInfo';
import { styles } from './ComponentNodeUtil/CustomComponentNodeUtil';
import BaseComponent from './BaseComponent';
import { send_request } from '../../../../../../utils/Request';

const ProductionCanvasComponent = ({ id, data, classes, selected }) => {
    const { modeInfo } = useContext(CanvasContext);
    if (modeInfo === undefined) {
      return <BaseComponent classes={classes} data={data} isConnectable={isConnectable} />;
    }

    const location = useLocation();
    const search = useLocation().search;
    const queryParams = new URLSearchParams(search);

    const parentRef = useRef(null);
    const ref = useRef(null);
    const nodes = useCanvasStore(s => s.nodes);
    const { edit, forceStart, handleProductionCanvasMount } = useContext(CanvasContext);
    const { productionVersion } = useContext(CanvasContext);
    const params = useParams();
    const projectId = params.id;
    const navigate = useNavigate();
    const [mode, setMode] = modeInfo;

    // State hooks
    const [editComponent, setEditComponent] = edit ? edit : useState({});
    const [forceStartComponent, setForceStartComponent] = forceStart ? forceStart : useState({});
    const [isSelectedComponent, setIsSelectedComponent] = useState(false);
    const [isSelectedForceStart, setIsSelectedForceStart] = useState(false);
    const [svgSelectedClass, setSvgSelectedClass] = useState('');
    const [showCheckingLatestDialog, setShowCheckingLatestDialog] = useState(false);
    const [draftVersion, setDraftVersion] = useState('1');
    const [isHovered, setIsHovered] = useState(false);

    // Effect hooks
    useEffect(() => {
      const dataComponentId = data && data.componentId ? data.componentId : id;
      setIsSelectedComponent(editComponent && dataComponentId === editComponent.componentId);
    }, [editComponent, data, id]);

    useEffect(() => {
      setIsSelectedForceStart(
        selected || (forceStartComponent && !selected && id === forceStartComponent.componentId)
      );
    }, [forceStartComponent, selected, id]);

    useEffect(() => {
      const isTrue = selected || isSelectedComponent || isSelectedForceStart;
      setSvgSelectedClass(isTrue ? classes.selectedIcon : '');
    }, [isSelectedComponent, isSelectedForceStart, selected, classes.selectedIcon]);

    function handleCloseForceStartPane() {
      if (window.location.href.includes('?force_start='))
        navigate(`/project/production/${projectId}`);
      setForceStartComponent(null);
    }
    const getLatestVersion = async projectId => {
      return send_request(`project-service/project/last-version/${projectId}`, '', '', 'get').then(
        res => {
          if (!res || !res.data) return;
          let { draftVersion } = res.data;
          if (draftVersion) {
            setDraftVersion(draftVersion);
            useProjectStore.setState({ draftVersion });
          }

          // Look for force_start pane open in URL
          let componentId = queryParams.get('force_start');
          if (!componentId) return;

          // Try to find component
          let openedComponent = useProjectStore.getState().canvas?.find(comp => comp.id === componentId);

          if (!openedComponent) {
            // Component doesn't exist in production version
            navigate(`/project/production/${projectId}`);
            return;
          }

          // Else we found it! Set it to force start state
          setForceStartComponent({
            componentId: componentId,
            ...openedComponent.data,
          });
        }
      );
    };

    const handleNodeClick = useCallback(
      event => {
        event.stopPropagation();
        if (forceStartComponent) {
          handleCloseForceStartPane();
          return;
        }

        queryParams.set('force_start', id);
        let newUrl = `${location.pathname}?${queryParams.toString()}`;
        navigate(newUrl);
        getLatestVersion(projectId);
      },
      [forceStart, forceStartComponent, data, id]
    );

    // Handlers
    const handlePreview = useCallback(() => {
      useProjectStore.setState({ production: true, canvasView: false });
      if (edit && !editComponent) {
        setEditComponent({ ...data, componentId: id });
      }
    }, [edit, editComponent, data, id]);

    const handleRun = useCallback(() => {
      if (!data) return;
      setShowCheckingLatestDialog(true);
    }, [data]);

    const handleOpenEditable = async () => {
      let url;

      if (data.fromWorkflowPath) url = `/project/canvas/${projectId}?view=${data.parentId}`;
      else {
        url = `/project/canvas/${projectId}?view=${id}`;
      }

      useProjectStore.setState({ canvasView: false, production: false });

      navigate(url);
    };

    const nodeSettingsMenuItems = [
      {
        title: 'Run from this component',
        text: 'Run',
        icon: <PlayCircleIcon />,
        onClick: handleRun,
      },
      {
        title: 'View component',
        icon: <PreviewIcon />,
        onClick: handlePreview,
      },
      {
        title: 'Edit this component',
        icon: <EditIcon />,
        onClick: handleOpenEditable,
      },
    ];

    return (
      <>
        <div
          draggable={false}
          ref={parentRef}
          onDragOver={event => {
            event.preventDefault();
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={classes.nodeWrapper}
        >
          <div onClick={handleNodeClick}>
            {data.error && <Error className={`error-text ${classes.downsize}`}></Error>}
            <ComponentNodeView
              classes={classes}
              data={data}
              selected={selected}
              isSelectedComponent={isSelectedComponent}
              isSelectedForceStart={isSelectedForceStart}
              isProdCanvas={true}
              mode={mode}
              forceStartComponent={forceStartComponent}
              isHovered={isHovered}
            />
          </div>

          <ComponentSettingsToolbar
            selected={selected || isHovered}
            actions={nodeSettingsMenuItems}
            classes={classes}
            data={data}
          />

          {data.instructions &&
            (data.instructions.buildInstruction || data.instructions.editInstruction) && (
              <InstructionsInfo data={data} parent={parentRef} textRef={ref} />
            )}
        </div>
        {/* Dialogs */}
        {showCheckingLatestDialog && (
          <CanvasReloadDialog
            projectId={projectId}
            currentProdVersion={productionVersion}
            setShowCheckingLatestDialog={setShowCheckingLatestDialog}
            data={data}
            id={id}
            forceStartComponent={forceStartComponent}
            setForceStartComponent={setForceStartComponent}
            handleProductionCanvasMount={handleProductionCanvasMount}
          />
        )}
      </>
    );
};

export default withStyles(styles)(ProductionCanvasComponent);
